import { TextInput } from "components/common";
import { WithErrorForm } from "components/common/WithErrorForm";
import { SelectGroupModal } from "components/login/SelectGroupModal";
import { TextWithIconButton } from "components/parts/Button/TextWithIconButton";
import { TextWithIconTypes } from "components/parts/Button/TextWithIconButton/TextWithIconButton";
import { useFormik } from "formik";
import { useAuth } from "providers/AuthProvider";
import { useConsoleState } from "providers/ConsoleStateProvider";
import { useLoadingState } from "providers/LoadingProvider";
import React from "react";
import { useHistory } from "react-router";
import { ConsoleRoleType, Group } from "types/graphqlapi";
import { getUserQuery, listSelectableGroupQuery } from "utils/apis/user";
import { sendConfirmationCode } from "utils/auth";
import { validationSchemaLogin } from "utils/validation";

import { Typography } from "../../../components/common/Typography/index";
import {
  StyledAnchor,
  StyledCard,
  StyledForm,
  StyledFormFooter,
  StyledFormHeader,
  StyledFormRow,
  StyledLayout,
} from "./styled";

type LoginFormValueType = {
  email: string;
  password: string;
};

const INITIAL_VALUES = { email: "", password: "" };

export const Login: React.FC = () => {
  const history = useHistory();
  const { userSignIn, userSignOut } = useAuth();
  const { setLoading } = useLoadingState();
  const {
    setGroup,
    setUserInfo,
    setEmail,
    setScreenName,
    setSubject,
    setSubjectFilter,
  } = useConsoleState();

  const [selectableGroupList, setSelectableGroupList] = React.useState<Group[]>(
    []
  );

  const [openSelectGroupModal, setOpenSelectGroupModal] =
    React.useState<boolean>(false);

  const selectGroup = (groupId: string, groupName: string) => {
    setGroup(groupId, groupName);
    history.push("/subject");
  };

  const closeModal = () => {
    setOpenSelectGroupModal(false);
  };

  const onSubmit = async (values: LoginFormValueType) => {
    try {
      setLoading(true);
      const signInResult = await userSignIn(values.email, values.password);
      if (!signInResult) {
        setLoading(false);
        return;
      }

      const queryResult = await listSelectableGroupQuery(values.email);

      if (!queryResult || queryResult.length === 0) {
        alert("ユーザーが登録されていません。");
        setLoading(false);
        return;
      }

      setEmail(values.email);

      // 権限がアドミニストレータとorアスマークユーザーの場合、emailに紐づくグループIDは0である
      if (queryResult.some((group) => group.id === "0")) {
        const loginUser = await getUserQuery(values.email, "0");
        if (!loginUser) {
          setLoading(false);
          return;
        }
        setUserInfo(
          loginUser?.id,
          loginUser?.name,
          loginUser?.role,
          loginUser.is_personal_data_handler || false
        );
        setGroup("", "");
        setSubject(undefined);
        setSubjectFilter({
          status: "ALL",
          marked: "MARKED",
          group: "ALL",
          groupName: "",
          isSelectGroup: false,
        });
        history.push("/subject");
      } else if (queryResult.length === 1) {
        const loginUser = await getUserQuery(values.email, queryResult[0].id);
        if (!loginUser) {
          setLoading(false);
          return;
        }
        setUserInfo(loginUser?.id, loginUser?.name, loginUser?.role, false);
        setGroup(queryResult[0].id, queryResult[0].name);
        setSubject(undefined);
        setSubjectFilter({
          status: "ALL",
          marked: "ALL",
          group: "ALL",
          groupName: "",
          isSelectGroup: false,
        });

        history.push("/subject");
      } else {
        setSelectableGroupList(queryResult);
        setOpenSelectGroupModal(true);
      }

      setLoading(false);
    } catch {
      alert("エラーが発生しました。");
      setLoading(false);
    }
  };

  const onClickInit = async (email: string) => {
    const res = confirm("確認コードをメールで送付します。");
    if (res) {
      try {
        setLoading(true);
        const result = await sendConfirmationCode(email);
        if (result === "Succeeded") {
          alert("確認コードをメールで送付しました。");
          return;
        }
        switch (result) {
          case "Required properties: email":
            alert("メールアドレスを入力してください。");
            break;
          case "The email doesn't exist":
            alert("メールアドレスが有効ではありません。");
            break;
          case "CodeDeliveryFailureException":
            // 検証コードの送信に失敗した場合に起こる。
            alert("コードの送信に失敗しました。");
            break;
          default:
        }
      } catch (e) {
        console.error(e);
        alert("コードの送信に失敗しました。");
      } finally {
        setLoading(false);
      }
    }
  };

  const formik = useFormik<LoginFormValueType>({
    initialValues: INITIAL_VALUES,
    validationSchema: validationSchemaLogin,
    onSubmit,
  });

  React.useEffect(() => {
    userSignOut();
    setScreenName("");
    setUserInfo("", "", ConsoleRoleType.GROUPUSER, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledLayout>
      <StyledCard>
        <StyledFormHeader>
          <Typography text="ログイン" fontSize="large" />
        </StyledFormHeader>
        <StyledForm onSubmit={formik.handleSubmit}>
          <StyledFormRow>
            <Typography text="メールアドレス" fontSize="large" />
            <WithErrorForm
              touched={formik.touched.email}
              errorMessage={formik.errors.email}
            >
              <TextInput
                {...formik.getFieldProps("email")}
                width="20rem"
                height="2.5rem"
                type="email"
                autocomplete="username"
                maxlength="50"
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledFormRow>
            <Typography text="パスワード" fontSize="large" />
            <WithErrorForm
              touched={formik.touched.password}
              errorMessage={formik.errors.password}
            >
              <TextInput
                {...formik.getFieldProps("password")}
                width="20rem"
                height="2.5rem"
                type="password"
                autocomplete="current-password"
                maxlength="15"
              />
            </WithErrorForm>
          </StyledFormRow>
          <StyledAnchor onClick={() => onClickInit(formik.values.email)}>
            <Typography
              text="初回認証・パスワードを忘れた方はこちら"
              fontSize="medium"
            />
          </StyledAnchor>
          <StyledFormFooter>
            <TextWithIconButton
              type="submit"
              variant="primary"
              size="xlarge"
              text="ログイン"
              iconName={TextWithIconTypes.ExternalLinkOutline}
            />
          </StyledFormFooter>
        </StyledForm>
      </StyledCard>
      {openSelectGroupModal && (
        <SelectGroupModal
          selectableGroupList={selectableGroupList}
          selectGroup={selectGroup}
          closeHandler={closeModal}
        />
      )}
    </StyledLayout>
  );
};
